import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';
import { authApi } from '@/api/auth.api';
import { useSession } from '@/app/session';
import './TwoFactorNudge.css';
import { useTranslation } from 'react-i18next';

const DISMISSED = 'df.mfaNudgeDismissed';

/**
 * Asks clinic staff to turn on two-step sign-in, once per session.
 *
 * Who sees it, and why only them: anyone with a staff row can reach patient
 * records, and a stolen password is the whole attack. A patient signing in to
 * the portal can reach their own file and nothing else, so the same prompt
 * would be noise on the screen of someone with nothing to protect but
 * themselves — and the portal is the surface where an unnecessary security
 * chore turns into a support call.
 *
 * Dismissal is deliberately per *session*, not permanent: this is a
 * suggestion that should come back at the next sign-in, and a "don't show
 * again" on a security prompt is a prompt that is never seen again. It is not
 * a wall — nothing here blocks the person from working.
 */
export function TwoFactorNudge() {
  const { t } = useTranslation('auth');
  const { user, tenant } = useSession();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISSED) === '1';
    } catch {
      // Private window, or storage blocked. Showing it is the safe default.
      return false;
    }
  });

  // Never at a demo clinic. Its logins are shared by everyone who opens the
  // link, so the API refuses to enrol there — and prompting somebody towards
  // a button that will turn them down is worse than staying quiet.
  const isDemo = tenant?.demo != null;
  const isStaff = user?.kind === 'staff' && !isDemo;

  const mfa = useQuery({
    queryKey: ['auth', 'mfa'],
    queryFn: authApi.mfaStatus,
    // Only asked for the people it could apply to, and not while dismissed.
    enabled: isStaff && !dismissed,
    staleTime: 5 * 60_000,
  });

  if (!isStaff || dismissed) return null;
  // Absent or still loading means "we do not know yet", and guessing wrong
  // here shows a security warning to someone who already did what it asks.
  if (!mfa.data || mfa.data.enabled) return null;

  function hide() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED, '1');
    } catch {
      // Nothing persists; it will simply reappear on the next render.
    }
  }

  return (
    <div className="mfa-nudge" role="status">
      <ShieldAlert size={18} aria-hidden className="mfa-nudge__icon" />
      <div className="mfa-nudge__text">
        <strong>{t('activeazaAutentificareaInDoi2')}</strong> {t('contulTauAreAcces')}
      </div>
      <Link className="mfa-nudge__action" to="/settings?tab=account" onClick={hide}>
        {t('activeaza')}
      </Link>
      <button type="button" className="mfa-nudge__close" onClick={hide} aria-label="Ascunde">
        <X size={16} aria-hidden />
      </button>
    </div>
  );
}
